import { useEffect, useMemo, useRef, useState } from 'react';

import { useStateMachineInput } from '@rive-app/react-canvas';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { RIVE_DEFAULTS } from '../../../../hooks/rive/constants';
import { useRiveTextUpdater } from '../../../../hooks/rive/useRiveTextUpdater';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { type Banner } from '../../../../services/ui';

import { createMainBannerCtaClick, createMainBannerCtaLabel } from './mainBannerAnalytics';
import styles from './RiveHeader.module.scss';
import { useBannerCta } from './useBannerCta';
import { useRiveBannersAnimation } from './useRiveBannersAnimation';
import { getRiveBackgroundUrl } from './utils';

const CTA_INPUT_NAME = 'cta_on';
const CTA_CLICK_EVENT_NAME = 'user_click';

interface RiveHeaderProps {
  bannerCC: Banner;
  bannerSC: Banner;
  animationOnly?: Banner | null;
  currentCoinType: string;
  bannerClassName?: string;
  fullHeightAnimation?: boolean;
  /** The banner's image, measured by GenericHeader for main_banner_impression. */
  bannerRef?: React.Ref<HTMLImageElement>;
}

const RiveHeader: React.FC<RiveHeaderProps> = observer(
  ({ bannerCC, bannerSC, animationOnly, currentCoinType, bannerClassName, fullHeightAnimation = false, bannerRef }) => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const riveFileUrlAnimationOnly =
      (isMobile && animationOnly?.mobileBanner?.animationUrl) || animationOnly?.desktopBanner?.animationUrl || '';
    const currentCoinTypeBanner = currentCoinType === 'GC' ? bannerCC : bannerSC;
    const bannerBackgroundUrl = getRiveBackgroundUrl(currentCoinTypeBanner, isMobile);

    const ref = useRef<HTMLDivElement>(null);

    const {
      showRive: showRiveAnimationOnly,
      RiveComponent: RiveComponentAnimationOnly,
      rive: riveAnimationOnly,
    } = useRiveBannersAnimation({
      riveFileUrl: riveFileUrlAnimationOnly,
      fullHeight: fullHeightAnimation,
    });

    const ctaAction = useBannerCta();
    const ctaActionRef = useRef(ctaAction);
    ctaActionRef.current = ctaAction;
    // Read at tap time from the Rive instance on screen now; button_text falls back to the constant label.
    const getCtaLabel = useMemo(() => createMainBannerCtaLabel(riveAnimationOnly ?? null), [riveAnimationOnly]);
    const getCtaLabelRef = useRef(getCtaLabel);
    getCtaLabelRef.current = getCtaLabel;
    const guardedCtaClick = useMemo(
      () =>
        createMainBannerCtaClick(
          () => ctaActionRef.current,
          () => getCtaLabelRef.current(),
        ),
      [],
    );

    useEffect(() => {
      if (!ref.current || !riveAnimationOnly) return;

      const ob = new IntersectionObserver(([entry]) => {
        if (!riveAnimationOnly) return;
        if (entry.isIntersecting) {
          riveAnimationOnly.play();
        } else {
          riveAnimationOnly.pause();
        }
      });

      ob.observe(ref.current);
      return () => ob.disconnect();
    }, [ref, riveAnimationOnly]);

    const isScInput = useStateMachineInput(riveAnimationOnly ?? null, RIVE_DEFAULTS.STATE_MACHINE_NAME, 'is_sc', true);

    useEffect(() => {
      if (showRiveAnimationOnly && isScInput) {
        isScInput.value = currentCoinType === 'SC';
      }
    }, [showRiveAnimationOnly, isScInput, currentCoinType]);

    useRiveTextUpdater({
      rive: showRiveAnimationOnly ? riveAnimationOnly : null,
      isRivePlaying: showRiveAnimationOnly,
    });

    const isCtaOn = ctaAction !== null;
    const [hasClickTrigger, setHasClickTrigger] = useState(false);

    useEffect(() => {
      if (!riveAnimationOnly) return;

      const ctaBoolean = riveAnimationOnly.viewModelInstance?.boolean(CTA_INPUT_NAME);
      const trigger = riveAnimationOnly.viewModelInstance?.trigger(CTA_CLICK_EVENT_NAME);
      const shouldEnable = Boolean(ctaBoolean) && Boolean(trigger) && isCtaOn;

      if (ctaBoolean) {
        ctaBoolean.value = shouldEnable;
      }
      setHasClickTrigger(shouldEnable);

      if (!shouldEnable || !trigger) return;

      trigger.on(guardedCtaClick);
      return () => trigger.off(guardedCtaClick);
    }, [riveAnimationOnly, isCtaOn, guardedCtaClick]);

    return (
      <>
        <img
          ref={bannerRef}
          alt="CrownCoinsCasino header"
          className={classNames(styles.banner, bannerClassName)}
          fetchpriority="high"
          src={bannerBackgroundUrl}
        />
        {showRiveAnimationOnly && (
          <div
            ref={ref}
            className={classNames(styles.riveOverlay, {
              [styles.riveOverlayFullHeight]: fullHeightAnimation,
              [styles.riveOverlayInteractive]: hasClickTrigger,
            })}
          >
            {showRiveAnimationOnly && (
              <RiveComponentAnimationOnly className={classNames(styles.animationContainer, styles.activeAnimation)} />
            )}
          </div>
        )}
      </>
    );
  },
);

export default RiveHeader;
